import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Wind,
  Leaf,
  Eye,
  Sparkles,
  Snowflake,
  Compass,
  Brain,
  CloudRain,
  Zap,
  Waves,
  ShieldCheck,
  Globe2,
  Boxes,
  Search,
  AlertCircle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Play,
  RotateCcw,
  Clock,
  ChevronDown,
  Sprout,
  HeartHandshake,
} from "lucide-react";
import coverBoxBreathing from "@/assets/grounding-covers/box-breathing-cover.png.asset.json";
import videoBoxBreathing from "@/assets/grounding-videos/box-breathing.mp4.asset.json";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ListenButton } from "@/components/ListenButton";
import { useFavoritesList } from "@/hooks/useFavorite";
import { useScrollTopOnChange } from "@/hooks/use-scroll-top-on-change";
import { GuidedAudioPlayer } from "@/components/GuidedAudioPlayer";
import { GROUNDING_VOICE_SCRIPTS } from "@/data/grounding-voice-scripts";


export const Route = createFileRoute("/grounding")({
  validateSearch: (search: Record<string, unknown>): { open?: string } => {
    const out: { open?: string } = {};
    if (typeof search.open === "string") out.open = search.open;
    return out;
  },
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Grounding — Return to the Present Moment · Recovery Trauma™" },
      {
        name: "description",
        content:
          "Trauma-informed grounding for overwhelm, anxiety, panic, flashbacks and dissociation. Choose how you feel, get gentle guided exercises with voice and visuals.",
      },
      { property: "og:title", content: "Grounding — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Return to the present moment. Choose what feels supportive right now.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/grounding" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/grounding" }],
  }),
  component: withMemberGate(GroundingPage, "Grounding"),
});

// ────────────────────────────────────────────────────────────────────
// Data
// ────────────────────────────────────────────────────────────────────

import { EXERCISES, type Exercise } from "@/data/grounding-exercises";



// ────────────────────────────────────────────────────────────────────
// Feelings
// ────────────────────────────────────────────────────────────────────

type Feeling = {
  id:
    | "anxious"
    | "overwhelmed"
    | "disconnected"
    | "panicky"
    | "frozen"
    | "thoughts";
  emoji: string;
  icon: typeof Eye;
  title: string;
  blurb: string;
  /** Shown after the member chooses this state, before any exercise starts. */
  note: string;
  accent: Exercise["accent"];
  recommended: string[]; // exercise ids
};

const FEELINGS: Feeling[] = [
  {
    id: "anxious",
    emoji: "😰",
    icon: Zap,
    title: "Anxious",
    blurb: "I feel tense, worried or on edge.",
    note:
      "Anxiety can leave the body alert and the mind racing ahead. A practice that gives your attention something simple and concrete may help you feel a little more settled.",
    accent: "sage",
    recommended: ["5-4-3-2-1", "feet-on-ground", "sensory-grounding"],
  },
  {
    id: "overwhelmed",
    emoji: "🌪️",
    icon: CloudRain,
    title: "Overwhelmed",
    blurb: "Everything feels like too much.",
    note:
      "When everything feels like too much, narrowing your attention to one small thing can feel more manageable than trying to take it all in.",
    accent: "lavender",
    recommended: ["object-grounding", "5-4-3-2-1", "self-soothe"],
  },
  {
    id: "disconnected",
    emoji: "🌊",
    icon: Waves,
    title: "Disconnected",
    blurb: "I feel distant, numb or not fully present.",
    note:
      "Feeling disconnected can make your surroundings, body or emotions seem far away. You might prefer a practice that gently brings attention toward something concrete around you, rather than inward.",
    accent: "blush",
    recommended: ["5-4-3-2-1", "orienting", "sensory-grounding"],
  },
  {
    id: "panicky",
    emoji: "⚡",
    icon: Sparkles,
    title: "Panicky",
    blurb: "My body feels highly activated.",
    note:
      "When the body is highly activated, some people find a strong, clear sensation easier to notice than a subtle one. Keep it gentle, and stop whenever you would like to.",
    accent: "gold",
    recommended: ["temperature", "body-anchors", "feet-on-ground"],
  },
  {
    id: "frozen",
    emoji: "🧊",
    icon: Snowflake,
    title: "Frozen or shut down",
    blurb: "I feel stuck, heavy or unable to respond.",
    note:
      "Feeling stuck or heavy is a common response, not a failure. Something small and physical — a change of temperature, a little movement — may feel more possible than anything effortful.",
    accent: "cream",
    recommended: ["temperature", "feet-on-ground", "orienting"],
  },
  {
    id: "thoughts",
    emoji: "💭",
    icon: Brain,
    title: "Caught in my thoughts",
    blurb: "I can't seem to get out of my head.",
    note:
      "When thoughts loop, giving your senses something specific to do may help your attention rest somewhere else for a moment.",
    accent: "warm",
    recommended: ["object-grounding", "orienting", "sensory-grounding"],
  },
];

/** Library filters. An exercise can belong to more than one. */
const LIBRARY_FILTERS = [
  "Quick",
  "Sensory",
  "Movement",
  "External Focus",
  "Body-Based",
  "Guided",
] as const;
type LibraryFilter = (typeof LIBRARY_FILTERS)[number];

const EXERCISE_TAGS: Record<string, LibraryFilter[]> = {
  "5-4-3-2-1": ["Sensory", "External Focus", "Guided"],
  "self-soothe": ["Sensory", "Guided"],
  "safe-place": ["Guided"],
  "legs-up-the-wall": ["Movement", "Body-Based", "Guided"],
  "body-scan": ["Body-Based", "Guided"],
  progressive: ["Body-Based", "Movement", "Guided"],
  "feet-on-ground": ["Quick", "Body-Based"],
  orienting: ["External Focus", "Quick"],
  "sensory-grounding": ["Sensory", "External Focus"],
  "body-anchors": ["Quick", "Body-Based"],
  "object-grounding": ["Quick", "Sensory", "External Focus"],
  temperature: ["Quick", "Sensory", "Body-Based"],
};

const ACCENT: Record<
  Exercise["accent"],
  { bg: string; ring: string; chip: string; iconBg: string }
> = {
  sage: {
    bg: "bg-[color-mix(in_oklab,var(--sage)_18%,var(--background))]",
    ring: "ring-[color-mix(in_oklab,var(--sage-deep)_30%,transparent)]",
    chip: "bg-[color-mix(in_oklab,var(--sage)_22%,var(--background))] text-[var(--sage-deep)]",
    iconBg: "bg-[color-mix(in_oklab,var(--sage)_30%,var(--background))] text-[var(--sage-deep)]",
  },
  lavender: {
    bg: "bg-[color-mix(in_oklab,var(--lavender)_18%,var(--background))]",
    ring: "ring-[color-mix(in_oklab,var(--lavender-deep)_30%,transparent)]",
    chip: "bg-[color-mix(in_oklab,var(--lavender)_22%,var(--background))] text-[var(--lavender-deep)]",
    iconBg: "bg-[color-mix(in_oklab,var(--lavender)_30%,var(--background))] text-[var(--lavender-deep)]",
  },
  blush: {
    bg: "bg-[color-mix(in_oklab,var(--blush)_20%,var(--background))]",
    ring: "ring-[color-mix(in_oklab,var(--blush)_40%,transparent)]",
    chip: "bg-[color-mix(in_oklab,var(--blush)_24%,var(--background))] text-foreground",
    iconBg: "bg-[color-mix(in_oklab,var(--blush)_35%,var(--background))] text-foreground",
  },
  warm: {
    bg: "bg-[color-mix(in_oklab,var(--warm)_30%,var(--background))]",
    ring: "ring-[color-mix(in_oklab,var(--warm)_50%,transparent)]",
    chip: "bg-[color-mix(in_oklab,var(--warm)_35%,var(--background))] text-foreground",
    iconBg: "bg-[color-mix(in_oklab,var(--warm)_45%,var(--background))] text-foreground",
  },
  cream: {
    bg: "bg-[color-mix(in_oklab,var(--cream)_45%,var(--background))]",
    ring: "ring-border/60",
    chip: "bg-[color-mix(in_oklab,var(--cream)_55%,var(--background))] text-foreground",
    iconBg: "bg-[color-mix(in_oklab,var(--cream)_70%,var(--background))] text-foreground",
  },
  gold: {
    bg: "bg-[color-mix(in_oklab,var(--gold)_18%,var(--background))]",
    ring: "ring-[color-mix(in_oklab,var(--gold-deep)_30%,transparent)]",
    chip: "bg-[color-mix(in_oklab,var(--gold-light)_45%,var(--background))] text-[var(--gold-deep)]",
    iconBg: "bg-[color-mix(in_oklab,var(--gold)_28%,var(--background))] text-[var(--gold-deep)]",
  },
};

const FAV_KEY = "rt:fav-grounding";

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

const WHY_CARDS = [
  {
    emoji: "🌍",
    icon: Globe2,
    title: "Reconnect with the present",
    body: "Bring your attention toward your current surroundings when your mind feels somewhere else.",
    accent: "sage" as const,
  },
  {
    emoji: "🌊",
    icon: Waves,
    title: "Navigate overwhelm",
    body: "Give your attention something simple and concrete to focus on when everything feels like too much.",
    accent: "lavender" as const,
  },
  {
    emoji: "🧠",
    icon: Brain,
    title: "Build awareness",
    body: "Become more familiar with your body's signals and recognise when you might need additional support.",
    accent: "blush" as const,
  },
  {
    emoji: "🧰",
    icon: Boxes,
    title: "Build your personal toolkit",
    body: "Explore different techniques and discover which ones feel supportive for you.",
    accent: "gold" as const,
  },
];

// ────────────────────────────────────────────────────────────────────
// Collapsible info section — "Read more" keeps the page short on mobile
// ────────────────────────────────────────────────────────────────────

function InfoSection({
  id,
  title,
  emoji,
  teaser,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  emoji?: string;
  teaser?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = `${id}-body`;
  return (
    <section className="mb-6" aria-labelledby={id}>
      <div className="flex items-start justify-between gap-3 rounded-2xl border border-border/40 bg-background/60 p-4">
        <div className="min-w-0">
          <h2
            id={id}
            className="font-serif text-lg leading-snug text-foreground sm:text-xl"
          >
            {emoji ? (
              <span className="me-1.5" aria-hidden>
                {emoji}
              </span>
            ) : null}
            {title}
          </h2>
          {!open && teaser ? (
            <p className="mt-1 text-xs leading-snug text-muted-foreground sm:text-sm">
              {teaser}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="inline-flex min-h-9 shrink-0 items-center gap-1 self-center rounded-full border border-border/60 px-3 text-xs font-medium text-[var(--gold-deep)] transition hover:bg-cream/60"
        >
          {open ? "Show less" : "Read more"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </div>
      {open ? (
        <div id={bodyId} className="mt-3">
          {children}
        </div>
      ) : null}
    </section>
  );
}

function GroundingPage() {
  const search = Route.useSearch();
  const [introOpen, setIntroOpen] = useState(false);
  const [feeling, setFeeling] = useState<Feeling["id"] | null>(null);
  const [active, setActive] = useState<Exercise | null>(null);
  // Each exercise opens its intro screen first; "Begin Exercise" advances
  // to the guided view.
  const [stage, setStage] = useState<"intro" | "guided">("intro");
  const [filter, setFilter] = useState<LibraryFilter | "All">("All");
  const [query, setQuery] = useState("");

  const checkInRef = useRef<HTMLElement | null>(null);
  const recRef = useRef<HTMLElement | null>(null);
  const libraryRef = useRef<HTMLElement | null>(null);

  // Every in-page change of view opens at the top of the new content.
  useScrollTopOnChange(`${active?.id ?? ""}|${stage}`);

  const openIntro = (ex: Exercise) => {
    setActive(ex);
    setStage("intro");
  };
  const closeActive = () => {
    setActive(null);
    setStage("intro");
  };
  // Deep-link support: /grounding?open=<id> opens that exercise's intro.
  useEffect(() => {
    if (!search.open) return;
    const ex = EXERCISES.find((e) => e.id === search.open);
    if (ex) {
      setActive(ex);
      setStage("intro");
    }
  }, [search.open]);

  const favs = useFavoritesList(FAV_KEY);

  const favExercises = useMemo(
    () =>
      favs
        .map((f) => EXERCISES.find((e) => e.id === f.id))
        .filter((e): e is Exercise => Boolean(e)),
    [favs],
  );

  const chosen = feeling ? FEELINGS.find((f) => f.id === feeling) ?? null : null;

  const recommended = useMemo(() => {
    if (!chosen) return [];
    return chosen.recommended
      .map((id) => EXERCISES.find((e) => e.id === id))
      .filter((e): e is Exercise => Boolean(e));
  }, [chosen]);

  const followAlong = useMemo(() => EXERCISES.filter((e) => e.video), []);

  const library = useMemo(() => {
    const q = query.trim().toLowerCase();
    return EXERCISES.filter((e) => {
      const tags = EXERCISE_TAGS[e.id] ?? [];
      if (filter !== "All" && !tags.includes(filter)) return false;
      if (!q) return true;
      return (
        e.title.toLowerCase().includes(q) ||
        e.blurb.toLowerCase().includes(q) ||
        e.bestFor.join(" ").toLowerCase().includes(q)
      );
    });
  }, [filter, query]);

  const scrollTo = (el: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (active) {
    if (stage === "intro") {
      return (
        <IntroView
          exercise={active}
          onBegin={() => setStage("guided")}
          onClose={closeActive}
        />
      );
    }
    return <GuidedView exercise={active} onClose={closeActive} />;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:py-10">
      {/* ── Hero (short) ─────────────────────────────────────── */}
      <header className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--sage)_25%,var(--background))] ring-1 ring-[color-mix(in_oklab,var(--sage-deep)_25%,transparent)]">
            <Leaf className="h-5 w-5 text-[var(--sage-deep)]" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-light leading-tight tracking-tight text-foreground sm:text-3xl">
              <span aria-hidden>🌿</span> Grounding
            </h1>
            <p className="text-sm leading-snug text-muted-foreground">
              Come back to the present moment, one step at a time.
            </p>
          </div>
        </div>

        <div className="space-y-2 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
          <p>
            Grounding practices can help bring your attention back to the
            present when you feel anxious, overwhelmed, disconnected or caught
            in your thoughts.
          </p>
          <p className="text-foreground">
            You don't need to force yourself to feel calm. Choose something
            that feels manageable right now.
          </p>
          {introOpen ? (
            <p>
              When you feel anxious, overwhelmed, emotionally flooded or
              disconnected, it can sometimes be difficult to feel anchored in
              the present. Grounding gives you something concrete to notice —
              your surroundings, senses, body or other things happening in the
              here and now.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setIntroOpen((v) => !v)}
          aria-expanded={introOpen}
          className="mt-1.5 inline-flex min-h-9 items-center gap-1 rounded-full px-2 text-xs font-medium text-[var(--gold-deep)] transition hover:bg-cream/60"
        >
          {introOpen ? "Show less" : "Read more"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${introOpen ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </header>


      {/* ── Check-in ─────────────────────────────────────────── */}

      <section
        ref={checkInRef}
        id="check-in"
        className="mb-8 scroll-mt-24"
        aria-labelledby="check-in-heading"
      >
        <h2
          id="check-in-heading"
          className="mb-1 font-serif text-xl text-foreground sm:text-2xl"
        >
          How are you feeling right now?
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Choose what feels closest. There are no wrong answers. We'll suggest a
          few practices you might want to explore.
        </p>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
          {FEELINGS.map((f) => {
            const a = ACCENT[f.accent];
            const isActive = feeling === f.id;
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  const next = isActive ? null : f.id;
                  setFeeling(next);
                  if (next)
                    window.setTimeout(() => scrollTo(recRef.current), 80);
                }}
                aria-pressed={isActive}
                className={`group flex min-h-[88px] flex-col items-start gap-1.5 rounded-2xl border p-3 text-start transition-all ${a.bg} ${
                  isActive
                    ? `border-transparent ring-2 ${a.ring} shadow-md`
                    : "border-border/40 hover:shadow-sm"
                }`}
              >
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full ${a.iconBg}`}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="text-sm font-medium text-foreground">
                  <span className="me-1" aria-hidden>
                    {f.emoji}
                  </span>
                  {f.title}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {f.blurb}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Personalised recommendations ─────────────────────── */}
      <section ref={recRef} className="mb-8 scroll-mt-24" aria-live="polite">
        {chosen && recommended.length > 0 && (
          <div className="rounded-3xl border border-border/40 bg-background/70 p-5 sm:p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              You chose
            </p>
            <h2 className="mt-1 font-serif text-xl text-foreground">
              <span className="me-1" aria-hidden>
                {chosen.emoji}
              </span>
              {chosen.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {chosen.note}
            </p>
            <p className="mt-4 text-sm font-medium text-foreground">
              You might try:
            </p>
            <ul className="mt-2 space-y-2">
              {recommended.map((ex) => (
                <li key={ex.id}>
                  <button
                    type="button"
                    onClick={() => openIntro(ex)}
                    className="group flex w-full items-center gap-3 rounded-2xl border border-border/60 bg-background/80 p-3 text-start transition hover:border-gold/50 hover:shadow-sm"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)]">
                      <ex.icon className="h-4 w-4 text-foreground" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">
                        {ex.title}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {ex.duration} · {ex.blurb}
                      </span>
                    </span>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs italic text-muted-foreground">
              Nothing starts until you choose it.
            </p>
          </div>
        )}
      </section>

      {/* ── Self-harm support ────────────────────────────────── */}
      <section className="mb-8">
        <Link
          to="/learn/self-harm-support"
          className="group flex items-center gap-3 rounded-3xl border border-rose-200/70 bg-rose-50/70 p-4 transition hover:border-rose-300 hover:shadow-sm dark:border-rose-400/20 dark:bg-rose-950/20 sm:p-5"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300">
            <HeartHandshake className="h-5 w-5" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-base font-medium text-foreground">
              Struggling with urges to self-harm?
            </span>
            <span className="block text-sm text-foreground/80">
              You're not alone — gentle support, ways to ride out an urge and a
              personal safety plan.
            </span>
          </span>
          <ChevronRight
            className="h-5 w-5 shrink-0 text-foreground/60 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>
      </section>

      {/* ── Favourites ───────────────────────────────────────── */}
      {favExercises.length > 0 && (
        <section className="mb-8">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="font-serif text-xl text-foreground">
              ❤️ Your favourite grounding tools
            </h2>
            <Link
              to="/favorites"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              All favourites →
            </Link>
          </div>
          <LargeExerciseCarousel
            exercises={favExercises}
            onBegin={openIntro}
            compact
          />
        </section>
      )}

      {/* ── My Regulation Compass ────────────────────────────── */}
      <section className="mb-6">
        <Link
          to="/regulation-compass"
          className="group flex items-center gap-4 rounded-2xl border border-[var(--gold-deep)]/35 bg-[var(--gradient-gold)] p-4 shadow-sm transition-all hover:shadow-md"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-background/70">
            <Compass className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] uppercase tracking-widest text-foreground/70">
              Not sure what you need?
            </span>
            <span className="block text-base font-medium text-foreground">
              My Regulation Compass
            </span>
            <span className="block text-sm text-foreground/80">
              Choose what feels closest to your experience right now and get a
              few gentle options.
            </span>
          </span>
          <ChevronRight
            className="h-5 w-5 shrink-0 text-foreground/60 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>
      </section>

      {/* ── Personal note from Jana (collapsible) ───────────── */}
      <InfoSection
        id="jana-note"
        title="A personal note from Jana"
        emoji="💛"
        teaser="“Grounding became especially important to me during times when I dissociated.”"
      >
        <div className="relative overflow-hidden rounded-3xl border border-gold/30 bg-[color-mix(in_oklab,var(--cream)_60%,var(--background))] p-5 sm:p-7">
          <span
            className="pointer-events-none absolute -top-4 start-4 font-serif text-7xl text-gold/25 sm:text-8xl"
            aria-hidden
          >
            “
          </span>
          <div className="relative space-y-3 font-serif text-sm leading-relaxed text-foreground/90 sm:text-base">
            <p>
              Grounding became especially important to me during times when I
              dissociated. When I felt disconnected from myself or what was
              around me, simple grounding exercises helped me reconnect with
              the present moment.
            </p>
            <p>
              I learned that I didn't always need to do something complicated.
              Sometimes noticing what I could see, feel or hear around me was
              enough to begin finding my way back.
            </p>
            <p>
              That's one of the reasons I wanted grounding to have its own
              space here. Try different practices and discover what feels
              supportive for you — because what helped me may not be what
              works best for you.
            </p>
          </div>
          <p className="relative mt-4 text-sm font-medium text-[var(--gold-deep)]">
            — Jana, Founder of Recovery Trauma™
          </p>
          <p className="relative mt-2 text-xs italic text-muted-foreground">
            This is Jana's personal lived experience, shared as encouragement —
            not clinical advice.
          </p>
        </div>
      </InfoSection>

      {/* ── Before you begin (collapsible) ──────────────────── */}
      <InfoSection
        id="before-you-begin"
        title="Before you begin"
        teaser="You're always in control — you can stop or skip any exercise at any time."
      >
        <div className="space-y-2 rounded-2xl border border-border/50 bg-background/70 p-5 text-sm leading-relaxed text-muted-foreground">
          <p>
            Grounding isn't a replacement for professional mental-health or
            medical care. These exercises are provided as self-guided wellbeing
            tools and may not feel helpful for everyone.
          </p>
          <p>
            You are always in control. You can stop an exercise, skip a step,
            keep your eyes open, change your position or return to what feels
            comfortable at any time.
          </p>
          <p>
            If an exercise increases distress, discomfort or disconnection,
            stop and choose something that feels safer, or seek appropriate
            professional support.{" "}
            <Link to="/crisis" className="text-primary hover:underline">
              Crisis resources
            </Link>
            .
          </p>
        </div>
      </InfoSection>

      {/* ── 30-Day Reset ─────────────────────────────────────── */}

      <section className="mb-8">
        <Link
          to="/nervous-system-reset"
          className="group flex items-center gap-4 rounded-2xl border border-[var(--gold-deep)]/35 bg-[var(--gradient-gold)] p-4 shadow-sm transition-all hover:shadow-md"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-background/70 text-xl">
            <Sprout className="h-5 w-5 text-primary" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] uppercase tracking-widest text-foreground/70">
              Want to go deeper?
            </span>
            <span className="block text-base font-medium text-foreground">
              Reset &amp; Regulate
            </span>
            <span className="block text-sm text-foreground/80">
              A gentle, self-paced journey to learn more about your patterns and
              build your own regulation toolkit.
            </span>
          </span>
          <ChevronRight
            className="h-5 w-5 shrink-0 text-foreground/60 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>
      </section>

      {/* ── Full library ─────────────────────────────────────── */}

      <section ref={libraryRef} className="mb-10 scroll-mt-24">
        <h2 className="mb-3 font-serif text-xl text-foreground sm:text-2xl">
          All grounding practices
        </h2>
        <div className="mb-3">
          <label htmlFor="grounding-search" className="sr-only">
            Search grounding practices
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="grounding-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search practices"
              className="min-h-11 w-full rounded-full border border-border/60 bg-background/80 ps-9 pe-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          {(["All", ...LIBRARY_FILTERS] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f as LibraryFilter | "All")}
              aria-pressed={filter === f}
              className={`min-h-9 rounded-full border px-3 text-xs font-medium transition ${
                filter === f
                  ? "border-transparent bg-foreground text-background"
                  : "border-border/60 bg-background/70 text-muted-foreground hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        {library.length === 0 ? (
          <p className="rounded-2xl border border-border/40 bg-background/60 p-5 text-sm text-muted-foreground">
            No practices match that yet. Try another filter or clear the
            search.
          </p>
        ) : (
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {library.map((ex) => (
              <li key={ex.id}>
                <CompactExerciseCard ex={ex} onOpen={() => openIntro(ex)} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Follow-along videos ──────────────────────────────── */}
      {followAlong.length > 0 && (
        <section className="mb-10" aria-labelledby="follow-along">
          <h2
            id="follow-along"
            className="mb-1 font-serif text-xl text-foreground sm:text-2xl"
          >
            <span aria-hidden>🎥</span> Follow-along grounding
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Guided practices you can follow at your own pace. Nothing plays
            automatically — you decide when to begin.
          </p>
          <ul className="mx-auto grid max-w-2xl gap-3 sm:max-w-none sm:grid-cols-2">
            {followAlong.map((ex) => (
              <li
                key={ex.id}
                className="overflow-hidden rounded-3xl border border-border/40 bg-background/70"
              >
                <div className="aspect-video overflow-hidden bg-background/40">
                  <ExerciseCover
                    src={ex.cover}
                    alt={`${ex.title} — guided practice thumbnail`}
                  />
                </div>
                <div className="p-4">
                  <p className="font-serif text-base text-foreground">
                    <span className="me-1" aria-hidden>
                      🌿
                    </span>
                    Follow along: {ex.title}
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {ex.whatIsThis}
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full border border-gold/30 bg-cream/60 px-2 py-0.5 text-[11px] text-[var(--gold-deep)]">
                    <Clock className="h-3 w-3" aria-hidden /> {ex.duration}
                  </p>
                  <button
                    type="button"
                    onClick={() => openIntro(ex)}
                    className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-foreground px-4 text-sm font-medium text-background transition-opacity hover:opacity-90"
                  >
                    <Play className="h-4 w-4 fill-current" aria-hidden /> Start
                    guided practice
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Why practise grounding (collapsible) ────────────── */}
      <InfoSection
        id="why-grounding"
        title="Why practise grounding?"
        teaser="Four gentle ways grounding may help."
      >
        <ul className="grid gap-2.5 sm:grid-cols-2 sm:gap-4">
          {WHY_CARDS.map((c) => {
            const a = ACCENT[c.accent];
            const Icon = c.icon;
            return (
              <li
                key={c.title}
                className={`flex items-start gap-3 rounded-2xl border border-border/40 p-3.5 sm:p-4 ${a.bg}`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${a.iconBg}`}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    <span className="me-1" aria-hidden>
                      {c.emoji}
                    </span>
                    {c.title}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                    {c.body}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </InfoSection>

      <p className="rounded-2xl border border-border/40 bg-[color-mix(in_oklab,var(--cream)_40%,var(--background))] p-5 text-center text-sm text-muted-foreground">
        These practices support — they don't replace — therapy or medical care.{" "}
        <Link to="/crisis" className="text-primary hover:underline">
          Crisis resources
        </Link>
        .
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Compact practice card — explanation lives on the intro screen
// ────────────────────────────────────────────────────────────────────

function CompactExerciseCard({
  ex,
  onOpen,
}: {
  ex: Exercise;
  onOpen: () => void;
}) {
  const a = ACCENT[ex.accent];
  const Icon = ex.icon;
  const tags = EXERCISE_TAGS[ex.id] ?? [];
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group flex h-full w-full items-start gap-3 rounded-2xl border border-border/40 p-3.5 text-start transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-sm ${a.bg}`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${a.iconBg}`}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">
          {ex.title}
        </span>
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          {ex.duration} · {tags[0] ?? "Grounding"}
        </span>
        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
          {ex.blurb}
        </span>
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]">
          Learn &amp; begin <ChevronRight className="h-3 w-3" aria-hidden />
        </span>
      </span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Large exercise carousel
// ────────────────────────────────────────────────────────────────────

function LargeExerciseCarousel({
  exercises,
  onBegin,
  compact = false,
}: {
  exercises: Exercise[];
  onBegin: (exercise: Exercise) => void;
  compact?: boolean;
}) {
  return (
    <div className="relative">
      <Carousel
        opts={{ align: "start", containScroll: "trimSnaps" }}
        className="w-full"
      >
        <CarouselContent className="-ms-3 sm:-ms-4">
          {exercises.map((ex) => (
            <CarouselItem
              key={ex.id}
              className="basis-[88%] ps-3 sm:basis-[70%] sm:ps-4 lg:basis-[58%]"
            >
              <ExerciseCard ex={ex} onBegin={() => onBegin(ex)} compact={compact} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="start-3 top-[38%] hidden h-10 w-10 border-border/60 bg-background/90 sm:inline-flex" />
        <CarouselNext className="end-3 top-[38%] hidden h-10 w-10 border-border/60 bg-background/90 sm:inline-flex" />
        <CarouselSwipeHint storageKey="grounding-exercises" />
      </Carousel>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Exercise card
// ────────────────────────────────────────────────────────────────────



function ExerciseCard({
  ex,
  onBegin,
  compact = false,
}: {
  ex: Exercise;
  onBegin: () => void;
  compact?: boolean;
}) {
  const a = ACCENT[ex.accent];
  const Icon = ex.icon;
  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-border/40 ${a.bg} transition-all hover:shadow-md`}
    >
      <div className="aspect-[1/1] overflow-hidden border-b border-border/30 bg-background/40 sm:aspect-[4/5]">
        <ExerciseCover src={ex.cover} alt={`${ex.title} cover illustration`} />
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="mb-3 flex items-start gap-3">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${a.iconBg}`}>
            <Icon className="h-6 w-6" aria-hidden />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-serif text-lg leading-tight text-foreground sm:text-xl">
                <span className="me-1" aria-hidden>{ex.emoji}</span>
                {ex.title}
              </h3>
              <FavoriteButton
                storageKey={FAV_KEY}
                id={ex.id}
                label={ex.title}
                sub="Grounding exercise"
                to="/grounding"
                size="sm"
              />
            </div>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
              {ex.blurb}
            </p>
          </div>
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${a.chip}`}>
            <Clock className="h-3 w-3" aria-hidden /> {ex.duration}
          </span>
          {!compact && (
            <>
              {ex.bestFor.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-background/60 px-2.5 py-1 text-xs text-muted-foreground ring-1 ring-border/50"
                >
                  💛 {tag}
                </span>
              ))}
            </>
          )}
        </div>

        <button
          onClick={onBegin}
          className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-4 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          <Play className="h-4 w-4 fill-current" aria-hidden /> Begin
        </button>
      </div>
    </article>
  );
}


// ────────────────────────────────────────────────────────────────────
// Branded cover image
// ────────────────────────────────────────────────────────────────────

function ExerciseCover({
  src,
  alt,
  className = "",
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`h-full w-full object-cover ${className}`}
    />
  );
}


// ────────────────────────────────────────────────────────────────────
// Intro view — shown before each exercise so users understand what they
// are about to do (what / how / duration / helpful for / Learn More).
// ────────────────────────────────────────────────────────────────────

function IntroView({
  exercise,
  onBegin,
  onClose,
}: {
  exercise: Exercise;
  onBegin: () => void;
  onClose: () => void;
}) {
  const a = ACCENT[exercise.accent];
  const Icon = exercise.icon;
  const [learnOpen, setLearnOpen] = useState(false);

  return (
    <div className={`min-h-dvh ${a.bg}`}>
      {/* Hero image first — the practice opens on its illustration, not on chrome. */}
      <div className="relative">
        <div className="aspect-[16/10] max-h-[46vh] w-full overflow-hidden sm:aspect-[21/9]">
          <ExerciseCover src={exercise.cover} alt={`${exercise.title} cover illustration`} />
        </div>
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/45 to-transparent"
          aria-hidden
        />
        <button
          onClick={onClose}
          className="absolute start-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-background/85 px-3 py-1.5 text-sm text-foreground shadow-sm backdrop-blur hover:bg-background"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="absolute inset-x-0 bottom-0 px-4 pb-5 text-center">
          <div className="mx-auto max-w-2xl">
            <h1 className="font-serif text-2xl leading-tight text-foreground sm:text-3xl">
              <span aria-hidden>{exercise.emoji}</span> {exercise.title}
            </h1>
            <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-muted-foreground">
              {exercise.blurb}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 pb-10 pt-5">



        {/* What is this? */}
        <section className="mb-5 rounded-2xl border border-border/40 bg-background/70 p-5 backdrop-blur">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            What is this?
          </h2>
          <p className="text-sm leading-relaxed text-foreground sm:text-base">
            {exercise.whatIsThis}
          </p>
        </section>

        {/* How does it work? — hidden for video-only exercises */}
        {!exercise.video && (
          <section className="mb-5 rounded-2xl border border-border/40 bg-background/70 p-5 backdrop-blur">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              How does it work?
            </h2>
            <p className="mb-3 text-sm text-muted-foreground">
              You will be invited to notice:
            </p>
            <ul className="space-y-2">
              {exercise.howItWorks.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-foreground sm:text-base"
                >
                  <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/60" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs italic text-muted-foreground">
              There is nothing you need to change. Simply notice what is present.
            </p>
          </section>
        )}

        {/* Duration + helpful for */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          <section className="rounded-2xl border border-border/40 bg-background/70 p-5 backdrop-blur">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Duration
            </h2>
            <p className="inline-flex items-center gap-2 font-serif text-xl text-foreground">
              <Clock className="h-5 w-5 text-foreground/60" aria-hidden />
              {exercise.duration}
            </p>
          </section>
          <section className="rounded-2xl border border-border/40 bg-background/70 p-5 backdrop-blur">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Helpful for
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {exercise.bestFor.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-background/80 px-2.5 py-1 text-xs text-foreground ring-1 ring-border/50"
                >
                  💛 {tag}
                </span>
              ))}
            </div>
          </section>
        </div>

        {/* Begin */}
        <div className="mb-4 flex justify-center">
          <button
            onClick={onBegin}
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-3 text-sm font-medium text-background shadow-sm transition-opacity hover:opacity-90 sm:text-base"
          >
            <Play className="h-4 w-4 fill-current" aria-hidden />
            Begin Exercise
          </button>
        </div>

        {/* Learn more */}
        {exercise.learnMore && (
          <div className="mb-2 text-center">
            <button
              onClick={() => setLearnOpen((o) => !o)}
              aria-expanded={learnOpen}
              className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              📖 {learnOpen ? "Hide details" : "Learn More"}
            </button>
            {learnOpen && (
              <p className="mx-auto mt-4 max-w-xl rounded-2xl border border-border/40 bg-background/70 p-5 text-start text-sm leading-relaxed text-foreground/90 backdrop-blur">
                {exercise.learnMore}
              </p>
            )}
          </div>
        )}

        <p className="mt-8 flex items-start gap-2 rounded-2xl border border-border/40 bg-background/50 p-4 text-xs text-muted-foreground">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-foreground/60" aria-hidden />
          <span>
            You can stop or step back at any time. There is no pressure to
            complete the exercise.
          </span>
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Guided view (one step at a time)
// ────────────────────────────────────────────────────────────────────


function GuidedView({
  exercise,
  onClose,
}: {
  exercise: Exercise;
  onClose: () => void;
}) {
  // Video-only exercises play the Recovery Trauma™ video as the whole experience.
  if (exercise.video) {
    return <VideoView exercise={exercise} onClose={onClose} />;
  }
  const a = ACCENT[exercise.accent];
  // Practices with a full spoken script are listened to, not read.
  const voiceScript = GROUNDING_VOICE_SCRIPTS[exercise.id];
  const [stepIdx, setStepIdx] = useState(0);
  const total = exercise.steps.length;
  const isLast = stepIdx === total - 1;
  const progress = ((stepIdx + 1) / total) * 100;

  // gentle timer per step
  const perStep = Math.max(20, Math.round((exercise.minutes * 60) / total));
  const [seconds, setSeconds] = useState(perStep);
  useEffect(() => {
    setSeconds(perStep);
  }, [stepIdx, perStep]);
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [stepIdx]);

  const Icon = exercise.icon;

  return (
    <div className={`min-h-dvh ${a.bg}`}>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
        <button
          onClick={onClose}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to grounding
        </button>

        {/* Hero illustration block */}
        <div className="mb-6 overflow-hidden rounded-3xl border border-border/40 bg-background/40 backdrop-blur">
          <div className="aspect-[4/3] overflow-hidden bg-background/40">
            <ExerciseCover src={exercise.cover} alt={`${exercise.title} cover illustration`} />
          </div>
          {exercise.audio && (
            <audio
              key={exercise.audio}
              src={exercise.audio}
              controls
              preload="metadata"
              className="w-full"
            />
          )}
        </div>

        {/* Title + meta */}
        <div className="mb-4 flex items-center justify-between">
          <h1 className="font-serif text-2xl text-foreground sm:text-3xl">
            <span className="me-1" aria-hidden>{exercise.emoji}</span>
            {exercise.title}
          </h1>
          <FavoriteButton
            storageKey={FAV_KEY}
            id={exercise.id}
            label={exercise.title}
            sub="Grounding exercise"
            to="/grounding"
          />
        </div>

        {voiceScript ? (
          <>
            <GuidedAudioPlayer
              script={voiceScript}
              minutes={exercise.minutes}
              title={exercise.title}
              subtitle="Grounding practice"
            />
            <div className="mt-5 flex justify-center">
              <button
                onClick={onClose}
                className="inline-flex items-center gap-1 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90"
              >
                <Sparkles className="h-4 w-4" /> Complete
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Step {stepIdx + 1} of {total}</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {formatTime(seconds)}
              </span>
            </div>
            <div className="mb-8 h-1.5 w-full overflow-hidden rounded-full bg-background/60">
              <div
                className="h-full rounded-full bg-foreground/70 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <GuidedAudioPlayer
              script={exercise.steps}
              minutes={exercise.minutes}
              title={exercise.title}
              subtitle="Grounding practice"
              className="mb-6"
            />

            <div className="rounded-3xl border border-border/40 bg-background/70 p-6 backdrop-blur sm:p-8">
              <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                Step {stepIdx + 1}
              </p>
              <p className="font-serif text-xl leading-relaxed text-foreground sm:text-2xl">
                {exercise.steps[stepIdx]}
              </p>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                  disabled={stepIdx === 0}
                  className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-4 py-2 text-sm font-medium text-foreground transition-opacity disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                <div>
                  <ListenButton text={exercise.steps[stepIdx]} label="Listen" />
                </div>
                {isLast ? (
                  <button
                    onClick={onClose}
                    className="inline-flex items-center gap-1 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90"
                  >
                    <Sparkles className="h-4 w-4" /> Complete
                  </button>
                ) : (
                  <button
                    onClick={() => setStepIdx((i) => Math.min(total - 1, i + 1))}
                    className="inline-flex items-center gap-1 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90"
                  >
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}


        {/* Best for */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <span className="text-xs text-muted-foreground">Best for:</span>
          {exercise.bestFor.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full bg-background/70 px-2.5 py-1 text-xs text-muted-foreground ring-1 ring-border/50"
            >
              💛 {tag}
            </span>
          ))}
        </div>

        <p className="mt-8 flex items-start gap-2 rounded-2xl border border-border/40 bg-background/50 p-4 text-xs text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            If at any point you feel worse, gently stop and try a different practice or visit{" "}
            <Link to="/crisis" className="text-primary hover:underline">
              Crisis resources
            </Link>
            .
          </span>
        </p>
      </div>
    </div>
  );
}

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

// ────────────────────────────────────────────────────────────────────
// Video view — Recovery Trauma™ video as the whole experience.
// Controls limited to play, pause, restart and fullscreen.
// ────────────────────────────────────────────────────────────────────

function VideoView({
  exercise,
  onClose,
}: {
  exercise: Exercise;
  onClose: () => void;
}) {
  const a = ACCENT[exercise.accent];
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const restart = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    void v.play().catch(() => {});
  };

  const goFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    const anyV = v as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
    if (anyV.requestFullscreen) void anyV.requestFullscreen().catch(() => {});
    else if (anyV.webkitEnterFullscreen) anyV.webkitEnterFullscreen();
  };

  return (
    <div className={`min-h-dvh ${a.bg}`}>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        <button
          onClick={onClose}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to grounding
        </button>

        <div className="mb-4 flex items-center justify-between gap-3">
          <h1 className="font-serif text-2xl text-foreground sm:text-3xl">
            <span className="me-1" aria-hidden>{exercise.emoji}</span>
            {exercise.title}
          </h1>
          <FavoriteButton
            storageKey={FAV_KEY}
            id={exercise.id}
            label={exercise.title}
            sub="Grounding exercise"
            to="/grounding"
          />
        </div>

        <div className="overflow-hidden rounded-3xl border border-border/40 bg-black shadow-sm">
          <video
            ref={videoRef}
            src={exercise.video}
            poster={exercise.cover}
            controls
            controlsList="nodownload"
            playsInline
            preload="metadata"
            className="aspect-video w-full bg-black object-contain"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={restart}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-4 py-2 text-sm font-medium text-foreground hover:bg-background"
          >
            <RotateCcw className="h-4 w-4" /> Restart
          </button>
          <button
            type="button"
            onClick={goFullscreen}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-4 py-2 text-sm font-medium text-foreground hover:bg-background"
          >
            ⛶ Full Screen
          </button>
        </div>

        <p className="mt-8 flex items-start gap-2 rounded-2xl border border-border/40 bg-background/50 p-4 text-xs text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            You can pause or stop at any time. If you feel worse, gently step
            away and visit{" "}
            <Link to="/crisis" className="text-primary hover:underline">
              Crisis resources
            </Link>
            .
          </span>
        </p>
      </div>
    </div>
  );
}
